import React, { useEffect, useState } from 'react'
import {
  Box,
  Divider,
  Grid,
  makeStyles,
  Tab,
  Tabs,
  useTheme,
} from '@material-ui/core'
import TabPanel from 'components/material/TabPanel'
import { connect } from 'react-redux'
import { Translate } from 'react-localize-redux'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import TaskGroupsWidget from 'containers/TaskManagement/TaskGroupsWidget'
import useTeamFeature from 'hooks/useTeamFeature'
import TeaserChip from 'components/Teaser/TeaserChip'
import UpgradeMembershipOverlay from 'components/UpgradeMembership/UpgradeMembershipOverlay'
import PropTypes from 'prop-types'
import TrainingPlan from './TrainingSession'
import TrainingNotes from './TrainingNotes'
import GroupsAndParticipationContainer from './GroupsAndParticipation'
import TEAM_FEATURE_FLAGS from 'constants/teamFeatureFlags'

const useStyles = makeStyles({
  tabRoot: {
    '& .MuiTab-root': {
      padding: '6px 22px',
      maxWidth: 'none',
    },
    '& .MuiChip-colorPrimary': {
      marginLeft: 10,
    },
    '& .MuiChip-label': {
      padding: 0,
    },
  },
})

const TabViewContainer = ({
  currentTrainingUuid,
  participants,
  trainingDrills,
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [activeTab, setActiveTab] = useState('training-plan')
  const [players, setPlayers] = useState(participants || [])
  const [sessionDrills, setSessionDrills] = useState(trainingDrills || [])

  const { enabled: hasAdvancedTrainingFeaturesEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.advanced_training_features_enabled
  )

  const { enabled: hasTrainingTasksEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.task_manager_enabled
  )

  useEffect(() => {
    setSessionDrills(trainingDrills || [])
  }, [trainingDrills])

  const handlePlayersChange = (updatedPlayers) => {
    setPlayers(updatedPlayers)
  }

  const handleChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  const handleUpdateDrills = (updatedDrills) => {
    setSessionDrills(updatedDrills)
  }

  useEffect(() => {
    setPlayers(participants)
  }, [participants])

  return (
    <Box padding={2}>
      <Box style={{ padding: '0 16px' }}>
        <Tabs
          value={activeTab}
          onChange={handleChange}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : 'off'}
          indicatorColor="primary"
          className={classes.tabRoot}
        >
          <Tab
            label={
              <Grid alignItems="center" container justifyContent="center">
                <Grid item>
                  <Translate id="training.training-plan" />
                </Grid>
                {!hasAdvancedTrainingFeaturesEnabled && <TeaserChip />}
              </Grid>
            }
            value="training-plan"
          />
          <Tab
            label={
              <Grid alignItems="center" container justifyContent="center">
                <Grid item>
                  <Translate id="training.group-and-participation" />
                </Grid>
                {!hasAdvancedTrainingFeaturesEnabled && <TeaserChip />}
              </Grid>
            }
            value="training-groups"
          />
          <Tab
            label={
              <Grid alignItems="center" container justifyContent="center">
                <Grid item>
                  <Translate id="matches.match-notes" />
                </Grid>
                {!hasAdvancedTrainingFeaturesEnabled && <TeaserChip />}
              </Grid>
            }
            value="training-notes"
          />
          <Tab
            label={
              <Grid alignItems="center" container justifyContent="center">
                <Grid item>
                  <Translate id="menu.task" />
                </Grid>
                {!hasTrainingTasksEnabled && <TeaserChip />}
              </Grid>
            }
            value="training-tasks"
          />
        </Tabs>
        <Divider />
      </Box>
      <TabPanel value={activeTab} index="training-plan">
        <TrainingPlan
          sessionDrills={sessionDrills}
          onUpdateDrills={handleUpdateDrills}
        />
      </TabPanel>
      <TabPanel
        value={activeTab}
        index="training-groups"
        style={{ position: 'relative' }}
      >
        {!hasAdvancedTrainingFeaturesEnabled && <UpgradeMembershipOverlay />}

        <GroupsAndParticipationContainer
          players={players}
          onChange={handlePlayersChange}
        />
      </TabPanel>
      <TabPanel value={activeTab} index="training-notes">
        <TrainingNotes />
      </TabPanel>
      <TabPanel value={activeTab} index="training-tasks">
        <TaskGroupsWidget isTrainingEvent eventId={currentTrainingUuid} />
      </TabPanel>
    </Box>
  )
}
TabViewContainer.defaultProps = {
  participants: [],
  trainingDrills: [],
  currentTrainingUuid: '',
}

TabViewContainer.propTypes = {
  currentTrainingUuid: PropTypes.string,
  participants: PropTypes.arrayOf(
    PropTypes.shape({
      uuid: PropTypes.string.isRequired,
      name: PropTypes.string,
    })
  ),
  trainingDrills: PropTypes.arrayOf(PropTypes.shape({})),
}

export default connect(({ trainings: { current } }) => ({
  currentTrainingUuid: current.uuid,
  participants: current?.relationships?.participants,
  trainingDrills: current?.relationships?.drills,
}))(TabViewContainer)
